import React from 'react';
import { axios } from '../../../../services/axios';
import Papa from 'papaparse';
import { downloadCSV } from '../../../../utils/utils';
import { useSelector } from 'react-redux';
import Cookies from 'js-cookie';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
const FIELDS_TO_INCLUDE = {
    userPrincipalName: 'UPN',
    displayName: 'Display Name',
    telephoneNumber: 'Telephone Number',
    dialPlan: 'Dial Plan',
    voicePolicy: 'Voice Policy',
    usageLocation: 'Usage Location',
    managedBy: 'Managed By (TCAP/Other)',
    attribute1: 'Attribute 1',
    attribute2: 'Attribute 2',
    attribute3: 'Attribute 3',
};

const makeDateStr = () => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${yy}${mm}${dd}_${hh}${min}${ss}`;
};

const slugify = (str) =>
    (str || '').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');

// (3100): `selectedPlatformName` holds the raw platform key (used as a type
// discriminator elsewhere), so map it to a friendly label just for the filename.
const PLATFORM_FILENAME_LABELS = {
    ms_resource_account: 'Microsoft Teams RAs',
    ms_resource_account_gcch: 'Microsoft Teams GCC High RAs',
};

// Matches the Numbers CSV template naming pattern, e.g. Acme_TeamsUsers_
// 260806_130751.csv. (3100): 'Microsoft_Teams' is shortened to 'Teams'.
export const buildTemplateFilename = (companyName, platformName) => {
    const companySlug = slugify(companyName) || 'Company';
    const label = PLATFORM_FILENAME_LABELS[platformName] || platformName;
    const platformSlug = (slugify(label) || 'Users').replace(
        /^Microsoft_Teams/,
        'Teams',
    );
    return `${companySlug}_${platformSlug}_${makeDateStr()}.csv`;
};

/**
 * handles downloading user's CSV template
 */
export default function useCSVTemplate() {
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(false);
    const [jsonData, setJSONData] = React.useState([]);
    const [template, setTemplate] = React.useState(null);

    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { data: currentCompanyData } = useGetCurrentCompany();
    /**
     * transforms JSON Data from BE fields into more human readable CSV heading columns
     * @param {*} data
     * @returns
     */
    // Add this utility above or inside your file
    function sanitizeCSVField(value) {
        if (typeof value !== 'string' || value.length === 0) return value;
        const firstChar = value.charAt(0);
        // Check for =, +, -, @, tab, carriage return, and newline
        if (
            firstChar === '=' ||
            firstChar === '+' ||
            firstChar === '-' ||
            firstChar === '@' ||
            firstChar === '\t' ||
            firstChar === '\r' ||
            firstChar === '\n' ||
            firstChar === String.fromCharCode(9) || // tab
            firstChar === String.fromCharCode(10) || // newline (LF)
            firstChar === String.fromCharCode(13) // carriage return (CR)
        ) {
            return "'" + value;
        }
        return value;
    }

    const transformJSONData = (data) => {
        const platformId = Cookies.get('selectedPlatformId');
        const platformName = Cookies.get('selectedPlatformName');
        let Fields;
        if (
            platformId === '46140cdd-5134-4088-b474-9aad13987c9f' ||
            platformId === '86394d2a-acdd-4923-8623-dcae7541e641'
        ) {
            Fields = {
                userPrincipalName: 'UPN',
                displayName: 'Display Name',
                accountType: 'Account Type',
                telephoneNumber: 'Telephone Number',
                licenses: 'Licenses',
                managedBy: 'Managed By (TCAP/Other)',
                attribute1: 'Attribute 1',
                attribute2: 'Attribute 2',
                attribute3: 'Attribute 3',
            };
        } else if (
            platformName === 'ms_resource_account' ||
            platformName === 'ms_resource_account_gcch'
        ) {
            Fields = {
                userPrincipalName: 'UPN',
                displayName: 'Display Name',
                telephoneNumber: 'Telephone Number',
                voicePolicy: 'Voice Policy',
                usageLocation: 'Usage Location',
                managedBy: 'Managed By (TCAP/Other)',
                attribute1: 'Attribute 1',
                attribute2: 'Attribute 2',
                attribute3: 'Attribute 3',
            };
        } else {
            Fields = FIELDS_TO_INCLUDE;
        }
        return data.map((user) => {
            return Object.keys(Fields).reduce((acc, key) => {
                const value = user[key];
                const newKey = Fields[key];
                // Always sanitize the value before assigning
                acc[newKey] = sanitizeCSVField(value);
                if (key === 'managedBy') {
                    acc[newKey] = value === 0 ? 'TCAP' : 'Other';
                }
                if (key === 'plan') {
                    acc[newKey] =
                        value === null &&
                        acc['Telephone Number'] !== 'Not Licensed' &&
                        acc['Managed By (TCAP/Other)'] === 'TCAP'
                            ? 'Company Default Plan'
                            : sanitizeCSVField(value);
                }
                return acc;
            }, {});
        });
    };

    /**
     * handles downloading user data from BE.
     */
    // const downloadUsersData = async () => {
    //     setLoading(true);
    //     try {
    //         console.log('here')
    //         // For testing with mocks
    //         // const usersResponse = mockUsersResponse;
    //         // const numbersResponse = mockNumbersResponse;
    //         //const platformId = Cookies.get('selectedPlatformId');
    //         // Comment out the actual API calls while testing

    //         const platformId = Cookies.get('selectedPlatformId');
    //         const [usersResponse, numbersResponse] = await Promise.all([
    //             axios.get(`/PlatformUsers/${currentCompany}/Platform/${platformId}`, {
    //                 params: {
    //                     usertype: Cookies.get('selectedPlatformName') === 'ms_resource_account' ? 'resourceaccount' : 'user'
    //                 }
    //             }),
    //             axios.get(`/company/${currentCompany}/platform/${platformId}/numbers/available`)
    //         ]);

    //         // Create numbers lookup
    //         const numbersLookup = numbersResponse.data.reduce((acc, number) => {

    //             acc[number.numberID] = number.telephoneNumber;
    //             return acc;
    //         }, {});

    //         // Transform users data with profile and number information
    //         const transformedData = usersResponse.data.platformUsers.map(user => {
    //             const userProfile = user.userProfiles[0]
    //             let telephoneNumber = '';

    //             // Handle different telephone number scenarios
    //             if (!user.isLicensed) {
    //                 telephoneNumber = 'Not Licensed';
    //             } else if (user.isLicensed && user.msCallingPlanEnabled && platformId === 'fac64979-385b-42ce-8b1c-d30e87822304') {
    //                 telephoneNumber = 'MS Calling Plan Assigned';
    //             } else {
    //                 telephoneNumber = numbersLookup[userProfile?.numberID] || '';
    //             }
    //             console.log('telephoneNumber', telephoneNumber)
    //            if(platformId==='46140cdd-5134-4088-b474-9aad13987c9f'){
    //             return {
    //                 id: user.id,
    //                 userPrincipalName: user.userPrincipalName,
    //                 displayName: user.displayName,
    //                 telephoneNumber,
    //                 managedBy: user.managedBy,
    //                 attribute1: user.platformAttribute1,
    //                 attribute2: user.platformAttribute2,
    //                 attribute3: user.platformAttribute3
    //             };
    //            }else{
    //             return {
    //                 id: user.id,
    //                 userPrincipalName: user.userPrincipalName,
    //                 displayName: user.displayName,
    //                 telephoneNumber,
    //                 dialPlan: user?.userProfiles?.length > 0 ? userProfile?.msDialPlan : '',
    //                 voicePolicy: user?.userProfiles?.length > 0 ? userProfile?.msVoicePolicy  : '',
    //                 usageLocation: user.usageLocation,
    //                 managedBy: user.managedBy,
    //                 plan: '',  // Keeping plan empty as per new structure
    //                 attribute1: user.platformAttribute1,
    //                 attribute2: user.platformAttribute2,
    //                 attribute3: user.platformAttribute3
    //             };
    //         }
    //         });

    //         const newJSONData = transformJSONData(transformedData);
    //         console.log('newJSONData', newJSONData);
    //         setJSONData(newJSONData);
    //     } catch (error) {
    //         setError(error);
    //         console.log(error)
    //         window.alert(
    //             'Could not download users data at this time. Please try again later.',
    //         );
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    // ... existing code ...

    // ... existing code ...

    // ... existing code ...
    const downloadUsersData = async () => {
        setLoading(true);
        try {
            const platformId = Cookies.get('selectedPlatformId');
            const isResourceAccount =
                Cookies.get('selectedPlatformName') === 'ms_resource_account' ||
                Cookies.get('selectedPlatformName') ===
                    'ms_resource_account_gcch';
            const is8x8Platform =
                platformId === '46140cdd-5134-4088-b474-9aad13987c9f' ||
                platformId === '86394d2a-acdd-4923-8623-dcae7541e641';
            const DR_SERVICE_TYPE = 'DirectRouting'; // Replace with your actual DR service type string
            const DR_GCCH_SERVICE_TYPE = 'DirectRoutingGCCH'; // GCC High service type
            // Fetch users, numbers, and all services
            const [
                usersResponse,
                numbersResponse,
                availableServicesResponses,
                // 8x8-only: a second, parallel fetch of PlatformUsers with no
                // `usertype` param. That param only supports 'user' /
                // 'resourceaccount' per the API's Swagger doc — omitting it
                // returns every user type, which is how existing Ring Groups
                // get included in the downloadable update template below.
                allUserTypesResponse,
            ] = await Promise.all([
                axios.get(
                    `/PlatformUsers/${currentCompany}/Platform/${platformId}`,
                    {
                        params: {
                            usertype: isResourceAccount
                                ? 'resourceaccount'
                                : 'user',
                        },
                    },
                ),
                axios.get(
                    `/company/${currentCompany}/platform/${platformId}/numbers/available`,
                ),
                axios.get(`/Services/Company/${currentCompany}`),
                is8x8Platform
                    ? axios.get(
                          `/PlatformUsers/${currentCompany}/Platform/${platformId}`,
                      )
                    : Promise.resolve(null),
            ]);

            // 8x8-only: merge existing Ring Groups into the platformUsers
            // list so the downloaded update template includes them as
            // editable rows (Number + Licences), same as a regular 8x8 User.
            // Forced to isLicensed: true to mirror RingGroupEditContent.jsx,
            // which has no license-gating concept for Ring Groups.
            if (is8x8Platform && allUserTypesResponse) {
                const existingIds = new Set(
                    usersResponse.data.platformUsers.map((u) => u.id),
                );
                const ringGroupUsers = (
                    allUserTypesResponse.data?.platformUsers || []
                )
                    .filter(
                        (u) =>
                            u.userType === 'RingGroup' &&
                            !existingIds.has(u.id),
                    )
                    .map((u) => ({ ...u, isLicensed: true }));
                usersResponse.data.platformUsers = [
                    ...usersResponse.data.platformUsers,
                    ...ringGroupUsers,
                ];
            }

            let availableDialPlansLookup = {};
            let availableVoicePoliciesLookup = {};
            let availableDPVPByService = {};

            if (
                platformId !== '46140cdd-5134-4088-b474-9aad13987c9f' &&
                platformId !== '86394d2a-acdd-4923-8623-dcae7541e641'
            ) {
                const isGCCHResourceAccount =
                    Cookies.get('selectedPlatformName') ===
                    'ms_resource_account_gcch';
                const individualServicesResponses = [];
                availableServicesResponses.data.forEach((v) => {
                    if (isGCCHResourceAccount) {
                        // Only include GCCH services for GCCH Resource Account
                        if (v.serviceType === DR_GCCH_SERVICE_TYPE) {
                            individualServicesResponses.push(
                                axios.get(`/service/${v.id}`),
                            );
                        }
                    } else if (
                        v.serviceType === DR_SERVICE_TYPE ||
                        v.serviceType === DR_GCCH_SERVICE_TYPE
                    ) {
                        individualServicesResponses.push(
                            axios.get(`/service/${v.id}`),
                        );
                    }
                });
                const servicesResponses = await Promise.all(
                    individualServicesResponses,
                );
                const allDRVPandDP = servicesResponses.map((v) => {
                    const voicePolicies = Object.fromEntries(
                        (v.data.voicePolicies || []).map((x) => [x.id, x.name]),
                    );
                    const dialPlans = Object.fromEntries(
                        (v.data.dialPlans || []).map((x) => [x.id, x.name]),
                    );
                    availableVoicePoliciesLookup = {
                        ...availableVoicePoliciesLookup,
                        ...voicePolicies,
                    };
                    availableDialPlansLookup = {
                        ...availableDialPlansLookup,
                        ...dialPlans,
                    };
                    return {
                        id: v.data.id,
                        serviceType: v.data.serviceType,
                        voicePolicies,
                        dialPlans,
                    };
                });
                availableDPVPByService = Object.fromEntries(
                    allDRVPandDP.map((x) => [x.id, x]),
                );
            }

            // Create numbers lookup
            const numbersLookup = numbersResponse.data.reduce((acc, number) => {
                acc[number.numberID] = number.telephoneNumber;
                return acc;
            }, {});

            // Transform users data with profile and number information
            const transformedData = usersResponse.data.platformUsers.map(
                (user) => {
                    const userProfile = user.userProfiles[0];
                    let telephoneNumber = '';
                    if (!user.isLicensed) {
                        telephoneNumber = 'Not Licensed';
                    } else if (
                        user.isLicensed &&
                        user.msCallingPlanEnabled &&
                        platformId === 'fac64979-385b-42ce-8b1c-d30e87822304'
                    ) {
                        telephoneNumber = 'MS Calling Plan Assigned';
                    } else if (
                        !user?.msCallingPlanEnabled &&
                        (!user?.userProfiles || user?.userProfiles.length === 0)
                    ) {
                        telephoneNumber = 'Not Allocated';
                    } else {
                        telephoneNumber =
                            userProfile?.platformNumber?.telephoneNumber || '';
                    }
                    // Map msDialPlan and msVoicePolicy IDs to names using the lookups
                    const msDialPlanName = userProfile?.msDialPlanID
                        ? availableDialPlansLookup[userProfile.msDialPlanID]
                        : '';
                    const msVoicePolicyName = userProfile?.msVoicePolicyID
                        ? availableVoicePoliciesLookup[
                              userProfile.msVoicePolicyID
                          ]
                        : '';
                    if (
                        platformId === '46140cdd-5134-4088-b474-9aad13987c9f' ||
                        platformId === '86394d2a-acdd-4923-8623-dcae7541e641'
                    ) {
                        return {
                            id: user.id,
                            userPrincipalName: user.userPrincipalName,
                            displayName: user.displayName,
                            accountType:
                                user.userType === 'RingGroup'
                                    ? 'Ring Group'
                                    : 'User',
                            telephoneNumber,
                            licenses: (userProfile?.licenses || [])
                                .map((l) => l.name)
                                .filter(Boolean)
                                .join(';'),
                            managedBy: user.managedBy,
                            attribute1: user.platformAttribute1,
                            attribute2: user.platformAttribute2,
                            attribute3: user.platformAttribute3,
                        };
                    } else if (isResourceAccount) {
                        return {
                            id: user.id,
                            userPrincipalName: user.userPrincipalName,
                            displayName: user.displayName,
                            telephoneNumber,
                            voicePolicy: msVoicePolicyName,
                            usageLocation: user.usageLocation,
                            managedBy: user.managedBy,
                            attribute1: user.platformAttribute1,
                            attribute2: user.platformAttribute2,
                            attribute3: user.platformAttribute3,
                        };
                    } else {
                        return {
                            id: user.id,
                            userPrincipalName: user.userPrincipalName,
                            displayName: user.displayName,
                            telephoneNumber,
                            dialPlan: msDialPlanName,
                            voicePolicy: msVoicePolicyName,
                            usageLocation: user.usageLocation,
                            managedBy: user.managedBy,
                            attribute1: user.platformAttribute1,
                            attribute2: user.platformAttribute2,
                            attribute3: user.platformAttribute3,
                        };
                    }
                },
            );
            const newJSONData = transformJSONData(transformedData);
            setJSONData(newJSONData);
        } catch (error) {
            setError(error);
            window.alert(
                'Could not download users data at this time. Please try again later.',
            );
        } finally {
            setLoading(false);
        }
    };
    // ... existing code ...
    /**
     * converts the template from JSON into CSV
     */
    const processTemplate = () => {
        const res = Papa.unparse(jsonData);
        setTemplate(res);
        const platformName = Cookies.get('selectedPlatformName') || 'Users';
        downloadCSV(
            res,
            buildTemplateFilename(currentCompanyData?.name, platformName),
        );
    };
    /**
     * handle download template UI button
     */
    const handleDownloadTemplate = () => {
        downloadUsersData();
    };

    React.useEffect(() => {
        //after data downloaded from BE, convert to CSV
        if (jsonData.length > 0) {
            processTemplate();
        }
    }, [jsonData]);

    const { darkMode } = useSelector((state) => {
        return { ...state.settings };
    });

    return {
        handleDownloadTemplate,
        loading,
        error,
        template,
    };
}
